<script setup lang="ts">
import { ref, onMounted, nextTick, watch } from "vue";
import POSView from "./POSView.vue";
import ShiftEndReportModal from "./components/ShiftEndReportModal.vue";
import CashFloatModal from "./components/CashFloatModal.vue";
import { api } from "../../../../api/Api";
import Toast from "../../../../services/Toast";
import { posStore } from '../../../../stores/posStore';
import ReportsMenu from '../../../../components/reports/ReportsMenu.vue'

import {
  CashierDuty,
  ItemCategory,
  ItemGroup,
  PaymentType,
  PosStation,
  RestaurantTables,
  SalesDocument,
  SalesInvoice,
  SalesRep,
} from "../../../../types/models";
import { tokenManager } from "../../../../api/utils/tokenManager";
import MessageBox from "../../../../services/MessageBox";
import RegisterPosStation from "./components/RegisterPosStation.vue";
import IconComponent from "../../../../components/ui/common/IconComponent.vue";
import NoPermissions from "../../../../components/ui/common/NoPermissions.vue";
import { Permission, User } from "../../../../types/auth";
import { formatDate, hasPermission } from "../../../../utils/CommonFunctions";
import Buttons from "../../../../components/ui/form/Buttons.vue";
import DropDownButton from "../../../../components/ui/common/DropdownButton.vue";
import { useAuthStore } from "../../../../stores/auth";
import TodaysInvoices from "./components/TodaysInvoices.vue";
import TodaysStockIssues from "./components/TodaysStockIssues.vue";
import StockIssuesForm from "../../../../features/inventory/inventorymanagement/stockissues/StockIssuesForm.vue";
import PaymentReceiptsForm from "../../../../features/finance/paymentreceipts/paymentreceipts/PaymentReceiptsForm.vue";
import PosSalesInvoicesList from "./components/PosSalesInvoicesList.vue";
import PosSalesQuotationsList from "./components/PosSalesQuotationsList.vue";
import PosStockRequestList from "./components/PosStockRequestList.vue";
import POSStockTransferList from "./components/PosStockTransferList.vue";
import MyShiftEndReports from "./components/MyShiftEndReports.vue";
import RestaurantPos from "./RestaurantPos.vue";
import NotificationButton from "../../../../components/ui/notification/NotificationButton.vue";
import SalesOrdersDetails from "../salesorders/SalesOrdersDetails.vue";
import { useNotificationsStore } from "../../../../stores/notifications";


defineEmits<{
  (e: "refresh-tables"): void;
  (e: "create-bill", table: RestaurantTables): void;
}>();

const tabs = ref<SalesInvoice[]>([]);
const addNewInvoiceTab = () => {
  let count = tabs.value.length + 1;
  tabs.value.push({
    id: count,
    title: "POS " + count,
    invoice: {
      id: "",
      sales_quotations_id: "",
      sales_orders_id: "",
      sales_goods_deliverys_id: "",
      sales_channels_id: "",
      outlets_id: "",
      date_time: "",
      customers_id: "",
      delivery_date: "",
      delivery_address_id: "",
      credit_terms_id: "",
      sales_reps_id: "",
      prefix: "",
      suffix: "",
      ref_no: "",
      shift_end_reports_id: "",
      price_lists_id: "",
      currencys_id: "",
      warehouses_id: "",
      outlet_pos_stations_id: "",
      customer_reference: "",
      remarks: "",
      attachment: "",
      uid: "",
      status_id: "",
      sub_total: 0,
      discount_a: 0,
      value_before_tax: 0,
      tax_amount: 0,
      net_total: 0,
      due_date: "",
      exchange_rate: 0,
      to_deliver: false,
      users_id: 0,
      items: [],
      payments: [],
    } as SalesDocument,
  });
  switchTab(tabs.value.length - 1);
};

const selectedInvoiceTab = ref<SalesInvoice | null>(null);
const switchTab = (index: number) => {
  activeTabIndex.value = index;
  selectedInvoiceTab.value = tabs.value[index];
};

const toggleFullscreen = () => {
  isFullscreen.value = !isFullscreen.value;
};

const activeTabIndex = ref(0);
const pos = posStore();
const isFullscreen = ref(true);
const showCashFloatModal = ref(false);
const showShiftEndReport = ref(false);
const showShiftConfirmation = ref(false);

const showInvoiceList = ref(false);
const showQuotationList = ref(false);
const showMyShiftEndReports = ref(false);

const showPaymentReceiptsForm = ref(false);
const showInvoices = ref(false);

/* ───────────── Sales-order modal ─────────────
 * Driven by `useNotificationsStore().pendingSalesOrderUid`. When the
 * cashier clicks "View order" on a `sales_order` notification in the
 * drawer, the store sets the ref to the order's uid; the watcher below
 * fetches the row via the standard staff `sales-orders/record` endpoint
 * and surfaces it in a modal overlaying the POS cart. Closing the modal
 * (or hitting Esc through the SalesOrdersDetails close button) clears
 * the ref via `consumePendingSalesOrder()` so the next notification can
 * fire afresh. */
const notifications = useNotificationsStore();
const showOrderModal = ref(false);
const viewingOrder = ref<any | null>(null);
const viewingOrderLoading = ref(false);

watch(
  () => notifications.pendingSalesOrderUid,
  async (uid) => {
    if (!uid) return;
    viewingOrderLoading.value = true;
    showOrderModal.value = true;
    viewingOrder.value = null;
    try {
      // The staff `record` endpoint takes the row's uid in the `id` field
      // (POS convention — see SalesOrdersController::record). Returns the
      // SalesOrders row with eager-loaded items/customer/delivery_address/
      // sales_rep/user — exactly the shape SalesOrdersDetails consumes.
      const response = await api.post("clients/revenue/sales/sales-orders/record", { id: uid });
      if (response?.success && response.data) {
        viewingOrder.value = response.data;
      } else {
        Toast.show("Could not load the sales order.", "warning");
        showOrderModal.value = false;
      }
    } catch {
      Toast.show("Could not load the sales order.", "error");
      showOrderModal.value = false;
    } finally {
      viewingOrderLoading.value = false;
      // Consume the ref so a future click on the same uid still triggers
      // a fresh fetch + modal mount (watchers only fire on value change).
      notifications.consumePendingSalesOrder();
    }
  },
  // immediate:true is the bridge for cross-shell clicks: when a sales-order
  // notification is opened from the back-office /notifications page,
  // NotificationsView sets pendingSalesOrderUid BEFORE router.push('/pos').
  // Without `immediate`, the watcher would skip the first cycle here on
  // mount (value didn't "change") and the modal would never open. The early
  // `if (!uid) return` keeps the no-pending case a free no-op.
  { immediate: true },
);

function closeOrderModal() {
  showOrderModal.value = false;
  viewingOrder.value = null;
}

/**
 * Fired by SalesOrdersDetails after the cashier clicks "Create Invoice" and
 * the staff `SalesOrdersController::convert` endpoint returns a new
 * SalesInvoices row. Two side-effects worth running here:
 *   1. Pin the freshly-created invoice onto `viewingOrder.invoice` so the
 *      modal's "Invoice created — …" pill renders even if the parent
 *      lingers on screen for the 1.2s auto-close delay.
 *   2. Trigger a notification poll so any "sales_order → invoiced"
 *      follow-up dispatched by the connector lands in the inbox without
 *      waiting for the next 30s interval.
 */
function onOrderInvoiced(invoice: any) {
  if (viewingOrder.value && invoice) {
    viewingOrder.value = { ...viewingOrder.value, invoice };
  }
  // Best-effort — silently tolerated if polling hasn't started yet.
  try { notifications.poll(); } catch { /* tolerated */ }
}
const showStockIssueList = ref(false);
const showStockRequestList = ref(false);
const showStockTransferList = ref(false);
const showStockIssues = ref(false);
const stockIssueMode = ref<"create" | "edit" | "view" | "showQuickView" | "toggleStatus" | string>("create");
const selectedStockIssue = ref<SalesDocument | null>(null);

const endPoint = "revenue/sales/pos/";

const payment_types = ref<PaymentType[]>([]);
const item_categories = ref<ItemCategory[]>([]);
const item_groups = ref<ItemGroup[]>([]);
const delivery_staffs = ref<User[]>([]);
const posStation = ref<PosStation>();
const cashierDuty = ref<CashierDuty | null>(null);
const salesReps = ref<SalesRep[]>([]);

const checkAccess = async (station: PosStation) => {
  const response = await api.post(endPoint + "access", {
    outlets_id: station?.outlets_id,
    warehouses_id: station?.outlet.warehouses_id,
    price_lists_id: station?.outlet.price_lists_id,
    device_name: station?.device_name,
    uid: station?.uid,
    sync_time: tokenManager.getSyncTime(),
  });
  if (response.success == true) {
    tokenManager.setSyncTime(formatDate(new Date(), 'yyyy-mm-dd HH:MM:00'));
    isFullscreen.value = true
    noPermissions.value = false;
    cashierDuty.value = response.data.cashier_duty;
    tokenManager.setPosStation(response.data.pos_station);
    posStation.value = response.data.pos_station;
    restaurantTables.value = posStation.value?.outlet?.tables || []
    payment_types.value = response.lists.payment_types;
    item_categories.value = response.lists.item_categories;
    item_groups.value = response.lists.item_groups;
    delivery_staffs.value = response.lists.delivery_staffs;
    salesReps.value = response.lists.sales_reps;
    reports.value = response.data.reports || []
    showShiftConfirmation.value = true;
    addNewInvoiceTab();    

    //POS Store 
    pos.addNewPosInvoice();
  } else {
    isFullscreen.value = false
    noPermissions.value = true;
    if (response.message == 'POS Station could not be found.') {
      MessageBox.confirm({
        title: "Invalid POS Station",
        message:
          "This device is not recognized as a valid POS Station <br /> do you want to register now?",
        confirmText: "YES",
        cancelText: "NO",
      }).then((confirmed: any) => {
        if (confirmed) {
          showPosRegistration.value = true;
        }
      });
    } else {
      Toast.show("Could not verify access for the POS Counter", "warning");
    }
  }
};

const store = useAuthStore()
const logOut = async () => {
  await store.logout()
};

const noPermissions = ref(false);
const showPosRegistration = ref(false);
const permissions = ref<Permission[]>();

onMounted(async () => {
  permissions.value = tokenManager.getPermissions();
  let station = tokenManager.getPosStation();
  if (station) {
    checkAccess(station)
  } else {
    MessageBox.confirm({
      title: "POS Station not registered",
      message:
        "This device is not registered as a POS Station <br /> do you want to register now?",
      confirmText: "YES",
      cancelText: "NO",
    }).then((confirmed: any) => {
      if (confirmed) {
        showPosRegistration.value = true;
      }
    });
  }
});

const handleCashFloatComplete = (duty: CashierDuty) => {
  showCashFloatModal.value = false;
  cashierDuty.value = duty;
};

const handleShiftEndReport = () => {
  showShiftEndReport.value = true;
};

const handleShiftEndComplete = () => {
  
  cashierDuty.value = null;
  showShiftEndReport.value = false;
  showShiftConfirmation.value = true;
  // Navigate back after ending shift
  //window.location.href = "/";
};

const continueShift = () => {
  showShiftConfirmation.value = false;
};

const endcashierDuty = () => {
  showShiftConfirmation.value = false;
  showShiftEndReport.value = true;
};

const createInvoice = (data: any) => {
  pos.setQuotation(data)
  showQuotationList.value = false;
};

const reports = ref<any[]>([]);

const restaurantTables = ref<RestaurantTables[]>([]);

const selectedTable = ref<RestaurantTables | null>(null);
const selectTable = (table: RestaurantTables) => {
  selectedTable.value = table;
};

const restaurantBack = () => {
  selectedTable.value = null;
};


const restaurantPos = ref<InstanceType<typeof RestaurantPos> | null>(null)

const orderCreated = (tables: RestaurantTables[]) => {
  selectedTable.value = null;
  restaurantTables.value = tables;
  pos.voidInvoice();
}

const viewBill = async (table: RestaurantTables) => {
  selectedTable.value = null;
  await nextTick();
  restaurantPos.value?.viewBill(table);

}

watch(
  () => selectedTable.value,
  async (value) => {
    if (!value) {
      await nextTick() // wait for DOM to update
      restaurantPos.value?.setTables(restaurantTables.value);
    }
  }
)


const refreshTables = () => {
  restaurantPos.value?.refreshTables();
}

</script>

<template>
  <div
    :class="[
      isFullscreen ? 'fixed inset-0 bg-gray-50 dark:bg-gray-900 z-50' : '',
    ]"
  >
  <template v-if="!noPermissions">
    <!-- Shift Confirmation Dialog -->
    <div
      v-if="showShiftConfirmation"
      class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
    >
      <div
        class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full"
      >
        <div class="p-6">
          <h3 class="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Shift Details
          </h3>
          <div class="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 mb-6">
            <div class="space-y-2">
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400">Outlet:</span>
                <span class="font-medium">{{
                  posStation?.outlet.name
                }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400">POS Station:</span>
                <span class="font-medium">{{
                  posStation?.name
                }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400">User:</span>
                <span class="font-medium">{{
                  tokenManager.getUser()?.name
                }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400">Started On:</span>
                <span class="font-medium">{{
                  formatDate(cashierDuty?.started_time, "yyyy-mm-dd HH:MM")
                }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400"
                  >Float Amount:</span
                >
                <span class="font-medium">{{
                  cashierDuty?.float.reduce((acc, den) => acc + den.amount, 0)
                }}</span>
              </div>
            </div>
          </div>
          <div class="flex justify-end gap-3">
            <Buttons caption="End Shift" icon="CircleX" @click="endcashierDuty" v-if="cashierDuty"
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-red-800 border border-gray-300 dark:border-red-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
             />
            <Buttons caption="Enter Float" icon="Banknote" @click="showCashFloatModal = true" v-if="cashierDuty && (cashierDuty?.float?.length ?? 0) <= 0" />
            <Buttons caption="Continue Shift" icon="Banknote" @click="continueShift" v-if="cashierDuty && (cashierDuty?.float?.length ?? 0) > 0" 
              className="px-4 py-2 text-sm font-medium dark:text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              />
              <Buttons caption="Log Out" icon="LogOut" @click="logOut" 
              className="px-4 py-2 text-sm font-medium dark:text-white bg-red-600 rounded-lg hover:bg-red-700"
              />
          </div>
        </div>
      </div>
    </div>

    <!-- Only show main content after shift is started -->
    <template v-if="cashierDuty && !showShiftConfirmation">
      <div
        :class="[
          isFullscreen ? 'fixed inset-0 bg-gray-50 dark:bg-gray-900 z-50' : '',
        ]"
      >
        <div
          class="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 h-full"
        >
          <!-- Header with Tabs and Controls -->
          <div
            class="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-gray-700"
          >
            <!-- Tabs -->
            <div class="flex-1 flex overflow-x-auto">
              <template v-if="posStation?.outlet?.type == 'Restaurant'">
                <h2>Table Management</h2>
                <Buttons caption="Refresh" icon="RefreshCcw" @click="refreshTables"
                  className="ml-4 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"/>
              </template>
              <button
                v-for="(tab, index) in pos.posInvoices"
                :key="tab.id"
                v-else
                @click="pos.switchTab(index)"
                class="px-4 py-2 border-r border-gray-200 dark:border-gray-700 relative group"
                :class="[
                  pos.activeInvoiceIndex === index
                    ? 'bg-blue-50 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700',
                ]"
              >
                <span>{{ tab.title }}</span>
                <!-- Close button -->
                <button
                  v-if="pos.posInvoices.length > 1"
                  @click.stop="pos.closeTab(index)"
                  class="w-5 h-5 bg-gray-500 text-white rounded-full flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 hover:bg-red-500 transition-opacity float-right ml-1"
                >
                  ✕
                </button>
              </button>
            </div>

            <!-- Controls -->
            <div class="flex items-center gap-2 ml-4">
              <button
                @click="handleShiftEndReport"
                class="p-2 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
                title="Shift End Report"
              >
                <IconComponent name="ClipboardCheck" />
                <span class="text-sm">Shift Report</span>
              </button>
              <button
                @click="pos.addNewPosInvoice()"
                class="p-2 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                title="New POS Tab (Ctrl+N)"
              >
                <IconComponent name="Plus" />
              </button>

              <DropDownButton text="" :hide-arrow="true">
                <Buttons caption="Todays Invoices" v-if="hasPermission(permissions, 'Todays Invoices')" icon="Receipt"  @click="showInvoices = true"
                className="px-4 py-2 w-full text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-900 border-b border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700" />
                <Buttons caption="Receive Payments" v-if="hasPermission(permissions, 'Receive Payments')" icon="ReceiptText"  @click="showPaymentReceiptsForm = true"
                className="px-4 py-2 w-full text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-900 border-b border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700" />
                <Buttons caption="New Stock Issue" v-if="hasPermission(permissions, 'New Stock Issue')" icon="Grid2X2Plus" @click="showStockIssues = true" 
                className="px-4 py-2 w-full text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-900 border-b border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700" />
                
                <Buttons caption="Sales Invoices" v-if="hasPermission(permissions, 'Sales Invoices')" icon="Receipt"  @click="showInvoiceList = true"
                className="px-4 py-2 w-full text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-900 border-b border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700" />
                <Buttons caption="Sales Quotations" v-if="hasPermission(permissions, 'Sales Quotations')" icon="FileCheck"  @click="showQuotationList = true"
                className="px-4 py-2 w-full text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-900 border-b border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700" />

                <Buttons caption="Stock Requests" v-if="hasPermission(permissions, 'Stock Requests')" icon="Blocks" @click="showStockRequestList = true" 
                className="px-4 py-2 w-full text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-900 border-b border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700" />
                <Buttons caption="Stock Transfers" v-if="hasPermission(permissions, 'Stock Transfers')" icon="Blocks" @click="showStockTransferList = true" 
                className="px-4 py-2 w-full text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-900 border-b border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700" />

                <Buttons caption="Stock Issue List" v-if="hasPermission(permissions, 'Stock Issue List')" icon="Blocks" @click="showStockIssueList = true" 
                className="px-4 py-2 w-full text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-900 border-b border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700" />
                <Buttons caption="Shift End Reports" v-if="hasPermission(permissions, 'Shift End Reports')" icon="Proportions" @click="showMyShiftEndReports = true"
                className="px-4 py-2 w-full text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-900 border-b border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700" />
              </DropDownButton>

              <ReportsMenu :reports="reports"  id="table-report" v-if="reports.length > 0"/>

              <DropDownButton text="" icon="ChartSpline" :hide-arrow="true" v-if="reports.length > 0">
                <Buttons caption="Todays Invoices" icon="Receipt"  @click="showInvoices = true"
                className="px-4 py-2 w-full text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-900 border-b border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700" />
              </DropDownButton>
              
              <div class="flex items-center gap-2">
                <IconComponent name="User" />
                <span class="text-gray-600 dark:text-gray-400">{{tokenManager.getUser()?.name}}</span>
              </div>
              <div class="flex items-center gap-2">
                <IconComponent name="Computer" />
                <span class="text-gray-600 dark:text-gray-400">{{tokenManager.getPosStation()?.name}}</span>
              </div>
              <div class="flex items-center gap-2">
                <IconComponent name="Store" />
                <span class="text-gray-600 dark:text-gray-400">{{tokenManager.getPosStation()?.outlet.name}}</span>
              </div>
              <!-- Inbox bell — same Pinia store + 30 s polling loop powering
                   the back-office header so the cashier sees storefront
                   quotations, ecommerce orders, etc. without having to leave
                   the till. -->
              <NotificationButton />
            </div>
            <button
                @click="toggleFullscreen"
                class="ml-4 p-2 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                :title="
                  isFullscreen
                    ? 'Exit fullscreen (F8)'
                    : 'Enter fullscreen (F8)'
                "
              >
                <IconComponent :name="!isFullscreen ? 'Fullscreen' : 'Shrink'" />
              </button>
          </div>

          <!-- Active Tab Content -->
          <div class="h-[calc(100%-3.5rem)]">
            <POSView
              :is-fullscreen="isFullscreen"
              :delivery_staffs="delivery_staffs"
              v-if="(posStation && posStation.outlet && posStation.outlet.type == 'Shop') || selectedTable"
              :salesReps="salesReps"
              :pos-station="posStation"
              :cashierDuty="cashierDuty"
              :item_categories="item_categories"
              @order-created="orderCreated"
              @view-bill="viewBill"
              :item_groups="item_groups"
              :payment_types="payment_types"
              @back="restaurantBack"
            />
            <RestaurantPos
              :is-fullscreen="isFullscreen"
              :delivery_staffs="delivery_staffs"
              ref="restaurantPos"
              v-else-if="posStation && posStation.outlet && posStation.outlet.type == 'Restaurant'"
              @table-selected="selectTable"
              :salesReps="salesReps"
              :pos-station="posStation"
              :restaurantTables="restaurantTables"
              :cashierDuty="cashierDuty"
              :item_categories="item_categories"
              :item_groups="item_groups"
              :payment_types="payment_types"
            />
          </div>
        </div>
      </div>
    </template>

    <!-- ─────────── Sales-order modal ───────────
         Surfaced when the cashier clicks "View order" on a sales_order
         notification in the drawer. Frame is a fixed-position overlay so
         the underlying POS cart stays mounted (modal close returns the
         cashier to whatever they were doing). The SalesOrdersDetails
         component fills the white card and handles its own close button. -->
    <div
      v-if="showOrderModal"
      class="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-2 md:p-4"
      @click.self="closeOrderModal"
    >
      <div class="bg-white dark:bg-gray-900 rounded-lg shadow-2xl w-full max-w-5xl h-[92vh] overflow-hidden flex flex-col">
        <div v-if="viewingOrderLoading" class="flex-1 flex items-center justify-center text-gray-500 dark:text-gray-400">
          <IconComponent name="Loader2" :size="24" class="animate-spin mr-2" />
          Loading order…
        </div>
        <SalesOrdersDetails
          v-else-if="viewingOrder"
          :data="viewingOrder"
          title="Sales Order"
          @close="closeOrderModal"
          @invoice-created="onOrderInvoiced"
        />
      </div>
    </div>

    <!-- Shift End Report Modal -->
    <ShiftEndReportModal
      v-if="showShiftEndReport"
      :endPoint="endPoint"
      :cashier-duty="cashierDuty"
      :pos-station="posStation"
      @close="showShiftEndReport = false"
      @complete="handleShiftEndComplete"
    />

    <!-- Cash Float Modal -->
    <CashFloatModal
      v-if="showCashFloatModal"
      :pos-station="posStation"
      @started="handleCashFloatComplete"
      :cashier-duty="cashierDuty"
      :payment_types="payment_types"
      @close="$router.back()"
    />

    <TodaysInvoices
      v-if="showInvoices"
      :cashier-duty="cashierDuty"
      :pos-station="posStation"
      @close="showInvoices = false" />

      <TodaysStockIssues
      v-if="showStockIssueList"
      :cashier-duty="cashierDuty"
      :pos-station="posStation"
      @close="showStockIssueList = false" />


      <StockIssuesForm
      v-if="showStockIssues"
      :data="selectedStockIssue"
      :cashier-duty="cashierDuty"
      :pos-station="posStation"
      end-point="/clients/inventory/inventory-management/stock-issue"
      title="Stock Issue"
      :mode="stockIssueMode"
      @close="showStockIssues = !showStockIssues"
    />

    <PaymentReceiptsForm
      v-if="showPaymentReceiptsForm"
      :data="selectedStockIssue"
      :cashier-duty="cashierDuty"
      :pos-station="posStation"
      end-point="/clients/finance/collections/payment-receipts/"
      title="Receive Payments"
      mode="create"
      @close="showPaymentReceiptsForm = !showPaymentReceiptsForm"
    />

    <PosStockRequestList
      v-if="showStockRequestList"
      @close="showStockRequestList = false" />

    <POSStockTransferList
      v-if="showStockTransferList"
      @close="showStockTransferList = false" />

    <PosSalesInvoicesList
      v-if="showInvoiceList"
      :cashier-duty="cashierDuty"
      :pos-station="posStation"
      @close="showInvoiceList = false" />

    <PosSalesQuotationsList
      v-if="showQuotationList"
      @quotation="createInvoice"
      @close="showQuotationList = false" />

    <MyShiftEndReports
      v-if="showMyShiftEndReports"
      :cashierDuty="cashierDuty"
      @close="showMyShiftEndReports = false" />
    
  </template>
  <RegisterPosStation
      v-if="showPosRegistration"      
      @close="showPosRegistration = false"
    />
  <NoPermissions :modal="false" v-else-if="!showPosRegistration" />
  </div>
</template>
